import { isEmpty } from 'lodash';

import { OptionType } from 'dashboard/components/ReactSelect';
import { SelectOptionValue } from 'dashboard/components-lib/MultiSelect/MultiSelect';
import {
  adverseMediaTypeDisplayText,
  AdverseMediaTypeV2,
} from 'dashboard/models/report/adverse-media';
import {
  ALL_PEP_CLASSES,
  pepClassDisplayText,
  PoliticallyExposedPersonClass,
} from 'dashboard/models/report/politically-exposed-person';
import { ReportTemplate } from 'dashboard/models/report-template';
import { COUNTRIES, CountryCode } from 'lib/constants/locales';
import { ApiModel } from 'lib/interfaces/api';
import { Json } from 'lib/types';

export enum ReportFilterInputType {
  Text = 'text',
  Number = 'number',
  MultiSelect = 'multiselect',
  Select = 'select',
}
export interface ReportFilterCondition {
  [property: string]: Json;
  template: string;
  property: string;
  operator: string;
  values: string[];
}
export interface ReportFilterMeta {
  properties: OptionType<string>[];
  getOperators: (property: string) => OptionType<string>[];
  getInputDisplay: (property: string) => ReportFilterInputMeta;
  isValid: (row: ReportFilterCondition) => boolean;
}
export interface ReportFilterInputMeta {
  type: ReportFilterInputType;
  label?: string;
  options?: SelectOptionValue<string>[];
}

const defaultValidator = ({ property, operator, values }: ReportFilterCondition) =>
  !isEmpty(property) && !isEmpty(operator) && !isEmpty(values);

const singleInputValidator = (condition: ReportFilterCondition) =>
  defaultValidator(condition) && !isEmpty(condition.values[0]);

const WATCHLIST: ReportFilterMeta = {
  properties: [
    { label: 'All Match Types', value: 'all-match-types' },
    { label: 'Sanctions Match Types', value: 'sanctions-match-types' },
    { label: 'Warnings Match Types', value: 'warnings-match-types' },
    { label: 'PEPs Match Types', value: 'peps-match-types' },
    { label: 'Fitness & Probity Match Types', value: 'fitness-and-probity-match-types' },
    { label: 'Matched List Name', value: 'matched-list-name' },
  ],
  getOperators: (property) =>
    property === 'matched-list-name'
      ? [{ label: 'contains', value: 'contains' }]
      : [{ label: 'includes', value: 'includes' }],
  getInputDisplay: (property) =>
    property === 'matched-list-name' ? REPORT_FILTER_INPUT_TEXT : REPORT_FILTER_INPUT_MATCH_TYPES,
  isValid: ({ property, operator, values }) =>
    !isEmpty(property) && !isEmpty(operator) && (operator === 'includes' || !isEmpty(values)),
};

const ADVERSE_MEDIA: ReportFilterMeta = {
  properties: [
    { label: 'Matched Countries', value: 'matched-countries' },
    { label: 'Matched Categories', value: 'matched-categories' },
    { label: 'Match Types', value: 'match-types' },
  ],
  getOperators: () => [{ label: 'includes', value: 'includes' }],
  getInputDisplay: (property) => {
    if (property === 'match-types') {
      return REPORT_FILTER_INPUT_MATCH_TYPES;
    } else if (property === 'matched-countries') {
      return {
        type: ReportFilterInputType.MultiSelect,
        label: 'Countries',
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        options: Object.keys(COUNTRIES).map((key: CountryCode) => ({
          label: COUNTRIES[key],
          value: key,
        })),
      };
    } else {
      return {
        type: ReportFilterInputType.MultiSelect,
        label: 'Categories',
        options: Object.values(AdverseMediaTypeV2)
          .sort()
          .map((type) => ({ label: adverseMediaTypeDisplayText(type), value: type })),
      };
    }
  },
  isValid: ({ property, operator }) => !isEmpty(property) && !isEmpty(operator), // All values can be true because empty array means all values are selected
};

export const REPORT_FILTER_META: { [key in ReportTemplate['type']]?: ReportFilterMeta } = {
  [ApiModel.ReportTemplateWatchlist]: WATCHLIST,
  [ApiModel.ReportTemplateBusinessWatchlist]: {
    ...WATCHLIST,
    properties: [
      { label: 'All Match Types', value: 'all-match-types' },
      { label: 'Sanctions Match Types', value: 'sanctions-match-types' },
      { label: 'Warnings Match Types', value: 'warnings-match-types' },
      { label: 'Fitness & Probity Match Types', value: 'fitness-and-probity-match-types' },
      { label: 'Matched List Name', value: 'matched-list-name' },
    ],
  },
  [ApiModel.ReportTemplateAdverseMedia]: ADVERSE_MEDIA,
  [ApiModel.ReportTemplateBusinessAdverseMedia]: ADVERSE_MEDIA,
  [ApiModel.ReportTemplatePhoneNumber]: {
    properties: [{ label: 'Phone Risk Score', value: 'risk-score' }],
    getOperators: () => [
      { label: 'equals', value: '==' },
      { label: 'less than', value: '<' },
      { label: 'greater than', value: '>' },
      { label: 'is empty', value: 'is-empty' },
    ],
    getInputDisplay: () => REPORT_FILTER_INPUT_NUMBER,
    isValid: singleInputValidator,
  },
  [ApiModel.ReportTemplatePoliticallyExposedPerson]: {
    properties: [{ label: 'PEP Classes', value: 'pep-classes' }],
    getOperators: () => [{ label: 'includes', value: 'includes' }],
    getInputDisplay: () => ({
      type: ReportFilterInputType.MultiSelect,
      label: 'PEP Classes',
      options: ALL_PEP_CLASSES.map((pepClass: PoliticallyExposedPersonClass) => ({
        label: pepClassDisplayText(pepClass),
        value: pepClass,
      })),
    }),
    isValid: ({ property, operator }) => !isEmpty(property) && !isEmpty(operator),
  },
  [ApiModel.ReportTemplateEmailAddress]: {
    properties: [
      { label: 'Blocklisted', value: 'email-is-blocklisted' },
      { label: 'Deliverable', value: 'email-is-deliverable' },
      { label: 'Disposable', value: 'email-is-disposable' },
      { label: 'Spam', value: 'email-is-spam' },
      { label: 'Suspicious', value: 'email-is-suspicious' },
    ],
    getOperators: () => [{ label: 'equals', value: 'equals' }],
    getInputDisplay: () => REPORT_FILTER_INPUT_BOOLEAN,
    isValid: defaultValidator,
  },
  [ApiModel.ReportTemplateSynthetic]: {
    properties: [
      { label: 'Abuse Score', value: 'abuse-score' },
      { label: 'Third Party Fraud Score', value: 'third-party-synthetic-score' },
    ],
    getOperators: () => [
      { label: 'equals', value: '==' },
      { label: 'less than', value: '<' },
      { label: 'greater than', value: '>' },
    ],
    getInputDisplay: () => REPORT_FILTER_INPUT_NUMBER,
    isValid: singleInputValidator,
  },
  [ApiModel.ReportTemplateBusinessLookup]: {
    properties: [
      { label: 'EIN Match', value: 'match-status-ein' },
      { label: 'Match Strength', value: 'match-status-strength' },
    ],
    getOperators: () => [{ label: 'equals', value: 'equals' }],
    getInputDisplay: (property) => {
      if (property === 'match-status-strength') {
        return {
          type: ReportFilterInputType.Select,
          options: [
            { label: 'Match', value: 'match' },
            { label: 'Partial Match', value: 'partial_match' },
            { label: 'No Match', value: 'no_match' },
          ],
        };
      }
      return REPORT_FILTER_INPUT_BOOLEAN;
    },
    isValid: defaultValidator,
  },
  [ApiModel.ReportTemplateAddressLookup]: {
    properties: [
      { label: 'DPV CMRA', value: 'dpv-cmra' },
      { label: 'PO Box', value: 'po-box' },
      { label: 'Residential Delivery Indicator', value: 'rdi' },
    ],
    getOperators: () => [{ label: 'equals', value: 'equals' }],
    getInputDisplay: (property) => {
      if (property === 'rdi') {
        return {
          type: ReportFilterInputType.Select,
          options: [
            { label: 'Commercial', value: 'commercial' },
            { label: 'Residential', value: 'residential' },
            { label: 'Unknown', value: 'unknown' },
          ],
        };
      } else if (property === 'dpv-cmra') {
        return {
          type: ReportFilterInputType.Select,
          options: [
            { label: 'Yes', value: 'Y' },
            { label: 'No', value: 'N' },
            { label: 'Unknown', value: 'unknown' },
          ],
        };
      }
      return REPORT_FILTER_INPUT_BOOLEAN;
    },
    isValid: ({ property, operator, values }) =>
      !isEmpty(property) && !isEmpty(operator) && !isEmpty(values),
  },
};

const REPORT_FILTER_INPUT_TEXT = { type: ReportFilterInputType.Text };

const REPORT_FILTER_INPUT_NUMBER = { type: ReportFilterInputType.Number };

const REPORT_FILTER_INPUT_BOOLEAN = {
  type: ReportFilterInputType.Select,
  options: [
    { label: 'True', value: 'true' },
    { label: 'False', value: 'false' },
  ],
};

const REPORT_FILTER_INPUT_MATCH_TYPES = {
  type: ReportFilterInputType.MultiSelect,
  label: 'Match Types',
  options: [
    { label: 'Exact name', value: 'exact' },
    { label: 'AKA', value: 'aka' },
  ],
};
